import PropTypes from 'prop-types';
import React, {useState} from 'react';
import _ from 'underscore';

import * as benchlingUtil from 'BenchlingUtil';
import {NucleotideType, ResidueType} from 'biojs/BioConstants';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import {NucleotideTypeToggle} from 'molbio/NucleotideTypeToggle';
import notifications from 'notifications';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FileChooser from 'ui-library/FileChooser';
import FileList from 'ui-library/FileList';
import FileRow from 'ui-library/FileRow';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import Modal from 'ui-library/modals/Modal';
import * as ImportUtil from 'util/ImportUtil';
import * as net from 'util/Net';
import {GenericEvent} from 'util/Types';

export interface BulkReimportFromFilesProps {
  files: Array<File>;
  folderId: string;
  onReimport: (
    files: Array<{name: string; s3Location: string}>,
    folderId: string,
    nucleotideType?: NucleotideType
  ) => Promise<void>;
  residueType: ResidueType;
  nucleotideType?: NucleotideType;
}

export async function _bulkReimportFromFiles({
  files,
  folderId,
  onReimport,
  residueType,
  nucleotideType,
}: BulkReimportFromFilesProps): Promise<void> {
  try {
    // Show loading screen since this can take seconds
    LoadingScreen.show('Uploading...');
    // Upload all `files` to S3, and pass output `s3Locations` through to `onReimport`
    let uploadCount = 0;
    const s3Locations = await Promise.all(
      files.map((file) =>
        ImportUtil.upload(file).done(() => {
          uploadCount++;
          LoadingScreen.set(`Uploaded ${uploadCount} of ${files.length}`);
        })
      )
    );
    const fileJSONs = _.zip(files, s3Locations).map(([file, s3Location]) => ({
      name: file.name,
      s3Location,
    }));

    LoadingScreen.set(`Reimporting...`);
    await onReimport(fileJSONs, folderId, nucleotideType);

    const sequenceTypeDisplay = residueType === ResidueType.NUCLEOTIDE ? nucleotideType : residueType;

    notifications.success(
      `${files.length} ${sequenceTypeDisplay} ${benchlingUtil.pluralize(files.length, 'sequence')} reimported`
    );
  } catch (error) {
    if (net.isServerRequestError(error)) {
      notifications.notifyError(error, {duration: 0});
    } else {
      throw error;
    }
  } finally {
    LoadingScreen.hide();
  }
}

export interface BulkReimportSequencesModalProps {
  initialFolderId: string;
  onReimport: (
    files: Array<{name: string; s3Location: string}>,
    folderId: string,
    nucleotideType?: NucleotideType
  ) => Promise<void>;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  residueType: ResidueType;
}

/**
 * Allow user to pick files and folder. On submit, send both to reimport sequences in that folder from those
 * files.
 */
function BulkReimportSequencesModal({
  initialFolderId,
  onRequestClose,
  onRequestSubmit,
  onReimport,
  residueType,
}: BulkReimportSequencesModalProps): JSX.Element {
  const [selectedFiles, setSelectedFiles] = useState<Array<File>>([]);
  const [folderId, setFolderId] = useState<string>(initialFolderId);
  const [nucleotideType, setNucleotideType] = useState<NucleotideType | undefined>(
    residueType === ResidueType.NUCLEOTIDE ? NucleotideType.DNA : undefined
  );

  function _handleSelectFiles(...files: Array<File>): void {
    setSelectedFiles([...selectedFiles, ...files]);
  }

  function _handleFolderChange({target: {value: newFolderId}}: GenericEvent): void {
    setFolderId(newFolderId);
  }

  async function _handleSubmit(): Promise<void> {
    await _bulkReimportFromFiles({files: selectedFiles, folderId, onReimport, residueType, nucleotideType});
    onRequestSubmit();
  }

  const hasErrors = !(selectedFiles.length > 0 && folderId);
  const typeDisplay = residueType === ResidueType.AA ? residueType : 'DNA / RNA';
  const listItemsDisplay = [];
  if (residueType === ResidueType.AA) {
    listItemsDisplay.push('replace residues and annotations');
  } else {
    listItemsDisplay.push('replace bases, annotations and translations', 'remove parts and primers');
  }
  listItemsDisplay.push(
    'update the description and custom metadata fields',
    'update schema fields that do not link to other Benchling objects'
  );
  const analysesDisplay = residueType === ResidueType.AA ? 'alignments' : 'CRISPR and alignments';

  return (
    <Modal
      buttons={[{disabled: hasErrors, style: ButtonStyle.PRIMARY, submit: true, text: 'Reimport'}]}
      onRequestClose={onRequestClose}
      onRequestSubmit={_handleSubmit}
      title={`Bulk reimport existing ${typeDisplay} sequences`}
    >
      <p>
        Upload {typeDisplay} files with which to replace the existing {typeDisplay} sequences from the target
        folder. File names must exactly match the sequence names or aliases.
      </p>
      <p>The reimport will:</p>
      <ul>
        {listItemsDisplay.map((displayItem, index) => (
          <li key={index}>{displayItem}</li>
        ))}
      </ul>
      <p>
        Dropdown schema fields, link schema fields, such as entity and part links, and analyses, such as{' '}
        {analysesDisplay}, will be unaffected.
      </p>
      <FormGroup>
        <Label>{DEFAULT_PROJECT_FOLDER_LABEL_TEXT}</Label>
        <FolderSelector
          onChange={_handleFolderChange}
          showInventory={true}
          showRegistries={true}
          value={folderId}
          unsetOnFetchFail={true}
        />
      </FormGroup>
      {nucleotideType && (
        <NucleotideTypeToggle nucleotideType={nucleotideType} onChangeSelection={setNucleotideType} />
      )}
      <FormGroup>
        <Label>New {typeDisplay} files</Label>
        <FileList>
          {_.sortBy(selectedFiles, 'name').map((file) => (
            <FileRow
              key={_.uniqueId('file')}
              file={file}
              onRequestRemove={() => {
                // eslint-disable-next-line no-restricted-syntax
                setSelectedFiles(_.without(selectedFiles, file));
              }}
            />
          ))}
        </FileList>
        <FileChooser multiple={true} onSelectFiles={_handleSelectFiles} />
      </FormGroup>
    </Modal>
  );
}

BulkReimportSequencesModal.propTypes = {
  initialFolderId: PropTypes.string.isRequired,
  onReimport: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  onRequestSubmit: PropTypes.func.isRequired,
  residueType: PropTypes.oneOf(Object.values(ResidueType)).isRequired,
};

export default BulkReimportSequencesModal;
